import { FiltersFormValues } from '@pages/analytics/cdrs/sms/Filters';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { format, parse } from 'date-fns';
import { appConfig } from 'src/config';
import { CampaignLogStatus } from './campaigns';
import { AccountProfile } from 'src/types/accountProfile';
import { withSessionExpiredHandler } from 'src/api/common/baseQueryWithAuth';

export interface Customer {
  active: boolean;
  address: string;
  apiSmsSuccess: boolean;
  autoAcceptInvoice: boolean;
  billingCycle: null;
  billingInterval: number;
  birthday: null;
  blockOnCreditLimit: boolean;
  city: string;
  cmt: null;
  connectionType: null;
  correspondenceAddress: string;
  correspondenceCity: string;
  correspondenceCountry: string;
  correspondencePostalCode: string;
  correspondenceRegion: null;
  country: string;
  creditLimit: number;
  currency: string;
  currentPackage: null;
  customerInfo: null;
  customerType: string;
  customerUuid: string;
  defaultInvoiceRounding: boolean;
  email: string;
  endDate: null;
  firstMonth: null;
  firstName: null;
  id: number;
  invoiceDeliveryAddressSame: boolean;
  invoiceEmailAddress: null;
  invoiceRounding: null;
  language: string;
  lastName: null;
  mo_sessionEsme: null;
  mo_smscId: null;
  mobilePhone: null;
  mt_sessionEsme: null;
  mt_smscId: null;
  name: string;
  nextPackage: null;
  override2FASenderId: boolean;
  paymentTerms: number;
  postalCode: string;
  pricelist: unknown;
  pricelists: unknown[];
  pricingPackage: null;
  region: null;
  registrationNumber: null;
  resellerId: number;
  resellerSenderId2FA: string;
  sendInvoiceAutomatically: boolean;
  senderId2FA: null;
  shortName: string;
  smpp: null;
  smsVerificationRequired: boolean;
  smscId: null;
  startDate: string;
  staticSenderIdNumber: null;
  useCreditLimit: boolean;
  useGlobalPaymentTerms: null;
  useStaticSenderId: boolean;
  vatNumber: null;
  vatRate: number;
}

export interface SMSPricingEntry {
  breakout: string;
  currency: string;
  destinationName: string;
  includedInPackage: boolean;
  rate: number;
  _links: any;
}
export interface SMSPricing {
  currency: string;
  customPositionResources: unknown[];
  monthlySubscriptionFee: number;
  mtlVpnFee: number;
  name: string;
  oneOffConnectionFee: number;
  oneOffVpnFee: number;
  smsInPackage: number;
  validFrom: string;
  pricelistEntries: SMSPricingEntry[];
  pricelistEntryMobileOriginatedResources: SMSPricingEntry[];
}

export interface SMSDetailsDailyEntry {
  allSms: number;
  asr: number;
  spam: number;
  blocked: number;
  delivered: number;
  inPackage: number;
  noDlr: number;
  rejected: number;
  revenue: number;
  revenueCurrency: string;
  undelivered: number;
}

export type SMSDetailsDaily = Record<string, SMSDetailsDailyEntry>;

export enum SMSCountType {
  API = 'API',
  CHAT = 'CHAT',
  CMT = 'CMT',
  EMAIL2SMS = 'EMAIL2SMS',
  FALLBACK = 'FALLBACK',
  MO = 'MO',
  SMPP = 'SMPP',
  SMS2EMAIL = 'SMS2EMAIL',
  SYSTEM = 'SYSTEM',
  TEST = 'TEST',
  TEXT2SPEACH = 'TEXT2SPEACH',
  VIBER = 'VIBER',
  WHATSAPP = 'WHATSAPP',
}

export enum CDRStatus {
  ACCEPTED = 'ACCEPTED',
  ACK = 'ACK',
  BLOCKED_NOT_IN_PACKAGE = 'BLOCKED_NOT_IN_PACKAGE',
  BLOCKED_OVER_CREDIT_LIMIT = 'BLOCKED_OVER_CREDIT_LIMIT',
  CALLED = 'CALLED',
  DELETED = 'DELETED',
  DELIVERED = 'DELIVERED',
  DEMO_LIMIT_EXCEEDED = 'DEMO_LIMIT_EXCEEDED',
  ENROUTE = 'ENROUTE',
  ERROR = 'ERROR',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  LOGONLY = 'LOGONLY',
  NEW = 'NEW',
  NOT_WHITELISTED = 'NOT_WHITELISTED',
  OPT_OUT = 'OPT_OUT',
  PARTIALLY_DELIVERED = 'PARTIALLY_DELIVERED',
  PREPARED = 'PREPARED',
  READ = 'READ',
  REJECTED = 'REJECTED',
  SENT = 'SENT',
  SENT_NO_FINAL_DLR = 'SENT_NO_FINAL_DLR',
  SENT_UNDELIVERED = 'SENT_UNDELIVERED',
  SPAM = 'SPAM',
  UNDELIVERED = 'UNDELIVERED',
  UNKNOWN = 'UNKNOWN',
  TTL_EXPIRED = 'TTL_EXPIRED',
}
export interface CDR {
  account: null | string;
  campaignName: null | string;
  createDateTime: string;
  createDateTimestamp: number;
  currency: string;
  destinationName: string;
  flash: boolean;
  id: number;
  isMo: boolean;
  lastStatus: CDRStatus;
  lastStatusTime: string;
  lastStatusTimestamp: number;
  msgId: string;
  msgSmscId: string;
  name: string;
  numParts: number;
  rate: number;
  receiver: string;
  sender: string;
  senderEmail: null | string;
  service: SMSCountType;
}

export enum CDRExportRequestStatus {
  ACCEPTED = 'ACCEPTED',
  'IN PROGRESS' = 'IN PROGRESS',
  READY = 'READY',
}
export interface CDRExportRequest {
  createdDate: string;
  fileName: null | string;
  fileSize: null;
  status: CDRExportRequestStatus;
}

export type CampaignStats = {
  count: number;
  lastEvent: CampaignLogStatus;
}[];

export interface UserCountStats {
  campaignCount: number;
  customerCount: number;
  details: null;
  sendSmsCount: number;
  userCount: number;
}

export const portalApi = createApi({
  tagTypes: ['Customer', 'SMSPricing', 'SMSDetailsDaily', 'CDR', 'AccountProfile', 'UserCountStats'],
  baseQuery: withSessionExpiredHandler(fetchBaseQuery({
    baseUrl: `${appConfig.URL_REST}`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token');

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      // Legacy REST can return HAL+JSON; accept both to avoid 406 Not Acceptable
      if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json, application/hal+json');
      }
      return headers;
    },
  })),
  reducerPath: 'portalApi',
  endpoints: (builder) => ({
    getUser: builder.query<AccountProfile, void>({
      query: () => ({
        url: 'user',
      }),
      providesTags: [{ type: 'AccountProfile', id: 'user' }],
    }),
    updateUser: builder.mutation<AccountProfile, Partial<AccountProfile>>({
      query: (body) => ({
        url: 'user',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'AccountProfile', id: 'user' }],
    }),
    getCustomer: builder.query<Customer, number>({
      query: (id) => ({
        url: `customers/${id}`,
      }),
      providesTags: (result) => [{ type: 'Customer', id: result?.id }],
    }),
    getPricings: builder.query<SMSPricing[], void>({
      query: () => ({
        url: 'price-lists/profile',
        params: {
          page: 0,
          size: 1000,
          sort: 'currency,asc',
          full: true,
        },
      }),
      providesTags: ['SMSPricing'],
      transformResponse: (response: { _embedded: { pricelist: SMSPricing[] } }) => response._embedded.pricelist,
    }),
    getSMSDetailsDaily: builder.query<SMSDetailsDaily, { id: number; dateFrom: string; dateTo: string }>({
      query: ({ id, dateFrom, dateTo }) => ({
        url: `customers/${id}/smss-details-daily`,
        params: {
          dateFrom,
          dateTo,
        },
      }),
      providesTags: [{ type: 'SMSDetailsDaily', id: 'smsDetailsDaily' }],
    }),
    getSMSDetailsHourly: builder.query<SMSDetailsDaily, { id: number; date: string | null }>({
      query: ({ id, date }) => ({
        url: `customers/${id}/smss-details-daily/${date}`,
      }),
      providesTags: [{ type: 'SMSDetailsDaily', id: 'smsDetailsHourly' }],
    }),
    requestCDRExport: builder.query<CDRExportRequest, FiltersFormValues>({
      query: ({
        startDate,
        endDate,
        senderIds,
        destinations,
        services,
        momt,
        statuses,
        statusStartTime,
        statusEndTime,
      }) => ({
        url: 'export_cdrs_file_history/cdrs',
        params: {
          creationDateBefore: format(parse(startDate, 'yyyy-MM-dd', new Date()), "yyyy-MM-dd'T'00:00"),
          creationDateAfter: format(parse(endDate, 'yyyy-MM-dd', new Date()), "yyyy-MM-dd'T'00:00"),
          sender: senderIds.length === 1 ? senderIds[0] : undefined,
          destination: destinations.length === 1 ? destinations[0] : undefined,
          service: services.length === 1 ? services[0] : undefined,
          lastStatusTimeBefore: statusStartTime
            ? format(parse(statusStartTime, 'yyyy-MM-dd', new Date()), "yyyy-MM-dd'T'00:00")
            : undefined,
          lastStatusTimeAfter: statusEndTime
            ? format(parse(statusEndTime, 'yyyy-MM-dd', new Date()), "yyyy-MM-dd'T'00:00")
            : undefined,
          lastStatus: statuses.length === 1 ? statuses[0] : undefined,
          isMo: momt === 'BOTH' ? undefined : momt === 'MT' ? false : true,
        },
      }),
      providesTags: [{ type: 'CDR', id: 'cdrExportRequest' }],
      transformResponse: (response: CDRExportRequest) => response,
    }),
    getCDRsExportHistory: builder.query<CDRExportRequest[], void>({
      query: () => ({
        url: 'export_cdrs_file_history',
      }),
      transformResponse: (response: { _embedded: { cdrsExportHistoryResources: CDRExportRequest[] } }) =>
        response._embedded.cdrsExportHistoryResources,
    }),
    downloadCDRExport: builder.query<Blob, string>({
      query: (index) => ({
        url: `export_cdrs_file_history/${index}/download`,
        responseHandler: async (response) => await response.blob(),
      }),
    }),
    sendTestSMS: builder.query<void, { from: string; to: string; message: string }>({
      query: (body) => ({
        url: 'campaigns/send-test-sms',
        method: 'POST',
        body: {
          smsList: [body],
        },
      }),
    }),
    getCampaignStats: builder.query<CampaignStats, number>({
      query: (campaignId) => ({
        url: `campaigns/${campaignId}/stats`,
        params: {
          page: 0,
          size: 100,
          full: false,
        },
      }),
    }),
    // Backend may return 500 (SpEL: 'principal' on java.lang.Object[]) after Keycloak; UI should handle undefined.
    getUsersCount: builder.query<UserCountStats, void>({
      query: () => ({
        url: 'users/count',
      }),
      providesTags: [{ type: 'UserCountStats', id: 'userCountStats' }],
    }),
    getMonthlyTraffic: builder.query<(number | null)[], void>({
      query: () => ({
        url: 'price-lists/monthlyTraffic',
      }),
    }),
    getDailyTraffic: builder.query<(number | null)[], void>({
      query: () => ({
        url: 'price-lists/dailyTraffic',
      }),
    }),
  }),
});

export const {
  useGetCustomerQuery,
  useGetPricingsQuery,
  useGetSMSDetailsDailyQuery,
  useGetSMSDetailsHourlyQuery,
  useLazyRequestCDRExportQuery,
  useGetCDRsExportHistoryQuery,
  useLazyDownloadCDRExportQuery,
  useLazySendTestSMSQuery,
  useGetCampaignStatsQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useGetUsersCountQuery,
  useGetMonthlyTrafficQuery,
  useGetDailyTrafficQuery,
} = portalApi;
